/**
 * TCR-1952/TCR-1953 — quyền SỬA THEO FIELD của hồ sơ khách hàng, dùng chung cho cả 3 nơi mở form
 * sửa khách: modal ở `/customers`, form trang chi tiết, và modal "Thông tin khách hàng" ở `/pos`.
 *
 * Ba màn này trước đây mỗi nơi tự viết một biểu thức `hasPermission([...])` khác nhau — và màn
 * `/pos` thì KHÔNG viết gì cả (đúng bug TCR-1953: NVKD bỏ tick "Sửa khách hàng" vẫn sửa được
 * toàn bộ). Gom về đây để chỉ còn MỘT định nghĩa; thêm một quyền theo-field mới sau này chỉ phải
 * sửa một chỗ.
 *
 * Đây là tầng HIỂN THỊ, không phải tầng bảo mật: BE mới là nơi chặn thật
 * (`assertCustomerFieldPermissions` + `@RequirePermissions` trên route sổ địa chỉ). Mục đích ở FE
 * là đừng mời người dùng gõ vào ô chắc chắn sẽ bị 403.
 */

export type CustomerEditAbilities = {
  /** Sửa được mọi field hồ sơ (`customers.update`/`customers.manage`/wildcard). */
  canEditAll: boolean;
  /** Sửa được ô "Email xuất HĐ" (`tax_email`). */
  canEditInvoiceEmail: boolean;
  /** TCR-2511 — sửa được ô "Số điện thoại" (`phone`). */
  canEditPhone: boolean;
  /** Thêm/sửa/xoá địa chỉ trong sổ địa chỉ giao hàng của khách. */
  canEditDeliveryAddress: boolean;
  /** Có ít nhất một thứ sửa được — dùng để quyết định hiện/ẩn nút Lưu, icon Sửa. */
  canEditAnything: boolean;
};

export const CUSTOMER_FULL_UPDATE_KEYS = ['customers.manage', 'customers.update'] as const;
export const CUSTOMER_INVOICE_EMAIL_KEY = 'customers.invoice_email.update';
export const CUSTOMER_DELIVERY_ADDRESS_KEY = 'customers.delivery_address.update';
export const CUSTOMER_PHONE_KEY = 'customers.phone.update';

type HasPermission = (key: string | readonly string[]) => boolean;

/**
 * `hasPermission` là hàm lấy từ `useRbac()`. Nhận vào qua tham số (không tự gọi hook) để helper
 * còn dùng được trong hàm thuần và test không phải dựng context.
 */
export function customerEditAbilities(hasPermission: HasPermission): CustomerEditAbilities {
  const canEditAll = hasPermission(CUSTOMER_FULL_UPDATE_KEYS);
  // Quyền sửa toàn bộ đương nhiên phủ hai quyền theo-field — người có `customers.update` KHÔNG
  // phải tick thêm hai ô mới. Đây cũng đúng luật OR mà BE áp dụng.
  const canEditInvoiceEmail = canEditAll || hasPermission(CUSTOMER_INVOICE_EMAIL_KEY);
  const canEditDeliveryAddress = canEditAll || hasPermission(CUSTOMER_DELIVERY_ADDRESS_KEY);
  const canEditPhone = canEditAll || hasPermission(CUSTOMER_PHONE_KEY);
  return {
    canEditAll,
    canEditInvoiceEmail,
    canEditPhone,
    canEditDeliveryAddress,
    canEditAnything: canEditAll || canEditInvoiceEmail || canEditPhone || canEditDeliveryAddress,
  };
}

/**
 * Có được mở FORM sửa hồ sơ khách (`/customers/:id/edit`, modal "Chỉnh sửa thông tin khách hàng")
 * hay không.
 *
 * Cố ý KHÔNG tính `canEditDeliveryAddress`: sổ địa chỉ nằm ở tab riêng của màn chi tiết, không có
 * field nào trong form hồ sơ — cho vào đây là mở ra một form mà mọi ô đều khoá.
 *
 * Dùng cho CẢ cổng route (`PrivateRoute.requiredPermissions` là OR-gate) lẫn nút "Sửa": thiếu một
 * trong hai chỗ là vai trò thấy nút rồi ăn 403, hoặc sửa được mà không có đường vào.
 */
export function canOpenCustomerEditForm(abilities: CustomerEditAbilities): boolean {
  return abilities.canEditAll || abilities.canEditInvoiceEmail || abilities.canEditPhone;
}

/**
 * Field hồ sơ mở riêng theo từng quyền theo-field. Hai cách gọi tên vì payload ở `/pos` đã ở dạng
 * BE (snake_case) còn modal ở `/customers` gửi dạng FE (`toCustomerBE()` đổi sau).
 */
const FIELD_LEVEL_FIELDS: ReadonlyArray<{
  ability: 'canEditInvoiceEmail' | 'canEditPhone';
  snake: string;
  camel: string;
}> = [
  { ability: 'canEditInvoiceEmail', snake: 'tax_email', camel: 'taxEmail' },
  { ability: 'canEditPhone', snake: 'phone', camel: 'phone' },
];

/**
 * Lọc payload hồ sơ khách xuống đúng phần được phép ghi.
 *
 * Dùng ở màn `/pos`: form ở đó phục vụ CẢ đơn hàng lẫn hồ sơ khách, nên khi thiếu quyền vẫn phải
 * cho người dùng gõ (dữ liệu chạy vào đơn) nhưng KHÔNG được đẩy phần cấm lên `PUT /v1/customers/:id`
 * — đẩy lên là ăn 403 rồi FE nuốt thành toast khó hiểu, đúng thứ TCR-1953 phàn nàn.
 *
 * Trả `{}` nghĩa là không còn gì để ghi → nơi gọi nên bỏ luôn request.
 */
export function pickEditableCustomerFields<T extends object>(
  payload: T,
  abilities: CustomerEditAbilities,
  naming: 'snake' | 'camel' = 'snake',
): Partial<T> {
  if (abilities.canEditAll) return payload;
  const allowed = new Set<string>(
    FIELD_LEVEL_FIELDS.filter((f) => abilities[f.ability]).map((f) => f[naming]),
  );
  const result: Partial<T> = {};
  for (const key of Object.keys(payload) as (keyof T & string)[]) {
    if (allowed.has(key)) result[key] = payload[key];
  }
  return result;
}
